import React, { useCallback } from 'react';
import { ImageProps, SpacingName, TypographyName } from '@doveit/lego';
import { NextPage } from 'next';
import AppLayout from '../client/layouts/app-layout/AppLayout';
import InfoSection, { InfoSectionProps } from '../client/templates/common/info-section/InfoSection';
import PageSection from '../client/components/page-section/PageSection';
import PageHead, { PageHeadProps } from '../client/components/page-head/PageHead';
import { TextAlign } from '../client/types';
import { ReviewsSectionProps } from '../client/templates/common/reviews-section/ReviewsSection';
import PageLayout, { PageLayoutSize } from '../client/layouts/page-layout/PageLayout';
import Intro from '../client/components/intro/Intro';
import TrustpilotMicroReviewCount from '../client/components/trustpilot/trustpilot-microreview-count/TrustpilotMicroReviewCount';
import ListReviews from '../client/containers/list-reviews/ListReviews';
import CreateLeadSection from '../client/templates/common/create-lead-section/CreateLeadSection';
import { sendLeadSubmitEventAndRedirect } from '../client/utils/tracking';
import { withError, withGetServerSideError } from '../client/hocs/with-server-side-error/withServerSideError';
import { importDynamicData } from '../services/importDynamicData';
import { ReviewTarget } from '../domain/graphcms';

export interface OpinionsProps {
  SEO: PageHeadProps['SEO'],
  intro: {
    title: string,
    description: string,
    image: ImageProps,
  },
  form: {
    title: string,
    intro: string,
    label: string,
  },
  infoSection: InfoSectionProps,
  reviews: ReviewsSectionProps,
}

export const Opinions: NextPage<OpinionsProps> = ({
  SEO,
  intro,
  infoSection,
  reviews,
  form,
}) => {
  const onSuccess = useCallback(sendLeadSubmitEventAndRedirect, []);

  return (
    <AppLayout activePage="reviews">
      <PageLayout>
        <PageHead SEO={SEO} />

        <PageSection padding={[SpacingName.BIG, 0, SpacingName.BIGGER]}>
          <PageSection.Wrapper maxWidth={PageLayoutSize.L}>
            <Intro
              title={intro.title}
              description={intro.description}
              image={intro.image}
            />
          </PageSection.Wrapper>
        </PageSection>

        <PageLayout.Content>
          <PageLayout.Main>
            <PageSection
              backgroundColor="neutrals.lighter"
            >
              <PageSection.Wrapper>
                <PageSection.Title textAlign={TextAlign.CENTER}>
                  {reviews.title}
                </PageSection.Title>
              </PageSection.Wrapper>
              <PageSection.Wrapper withoutPad>
                <PageSection.Block>
                  <ListReviews
                    target={[ReviewTarget.COMPRATORE, ReviewTarget.VENDITORE]}
                    showLink
                  />
                </PageSection.Block>
                <PageSection.Block>
                  <TrustpilotMicroReviewCount />
                </PageSection.Block>
              </PageSection.Wrapper>
            </PageSection>

            <InfoSection
              title={infoSection.title}
              reasons={infoSection.reasons}
            />

            <PageSection padding={[0, 0, SpacingName.BIGGEST]}>
              <PageSection.Wrapper>
                <PageSection.Title
                  content={form.title}
                  textAlign={TextAlign.CENTER}
                />
                <PageSection.Description
                  text={form.intro}
                  textAlign={TextAlign.CENTER}
                  size={TypographyName.H4}
                  color="neutrals.dark"
                />
                <CreateLeadSection
                  title={form.label}
                  dataRef="lead-form-main"
                  dataTracking="lead-form-main-submit"
                  onSuccess={(lead) => onSuccess('main', lead)}
                />
              </PageSection.Wrapper>
            </PageSection>
          </PageLayout.Main>
        </PageLayout.Content>
      </PageLayout>
    </AppLayout>
  );
};

export const getServerSideProps = withGetServerSideError<OpinionsProps>(async () => {
  const [data, common] = await Promise.all([
    importDynamicData('pages/opinions.yml'),
    importDynamicData('common.yml'),
  ]);
  return {
    props: {
      ...data,
      reviews: common.reviews,
    },
  };
});

export default withError(Opinions);
